import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import Select from "react-select";
import "../../styles/Form.css";

function EditGroupAdmin() {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const BASE_URL = process.env.REACT_APP_API_BASE_URL;

  // ✅ Only treat as edit if groupId is a number
  const isEditMode = !isNaN(Number(groupId));

  const [group, setGroup] = useState({
    pg_name: "",
    pg_email: "",
    pg_status: 1,
    pg_notify_leads: 0,
  });
  const [allAdmins, setAllAdmins] = useState([]);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [loading, setLoading] = useState(isEditMode);

  // Fetch group details in edit mode
  useEffect(() => {
    if (isEditMode) {
      axios
        .get(`${BASE_URL}/api/admin-groups/${groupId}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        })
        .then((res) => setGroup(res.data))
        .catch((err) => console.error("Error fetching group", err))
        .finally(() => setLoading(false));

      axios
        .get(`${BASE_URL}/api/admin-groups/users-by-group/${groupId}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        })
        .then((res) => setSelectedUserIds(res.data.map((u) => u.id)))
        .catch((err) => console.error("Error fetching group users", err));
    } else {
      setLoading(false);
    }
  }, [isEditMode, groupId, BASE_URL]);

  // Fetch all admins (only in edit mode)
  useEffect(() => {
    if (isEditMode) {
      axios
        .get(`${BASE_URL}/api/dropdowns/admin`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        })
        .then((res) => setAllAdmins(res.data))
        .catch((err) => console.error("Error fetching admins", err));
    }
  }, [isEditMode, BASE_URL]);

  const handleChange = (field, value) => {
    setGroup((prev) => ({ ...prev, [field]: value }));
  };

  const handleSelectChange = (selectedOptions) => {
    const ids = selectedOptions.map((opt) => opt.value);
    setSelectedUserIds(ids);
  };

  const handleSave = async () => {
    try {
      let activeGroupId = groupId;

      if (isEditMode) {
        await axios.put(`${BASE_URL}/api/admin-groups/${groupId}`, group, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
      } else {
        const res = await axios.post(`${BASE_URL}/api/admin-groups`, group, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        activeGroupId = res.data.insertId;
      }

      // Only update assigned users in edit mode
      if (isEditMode) {
        const res = await axios.get(
          `${BASE_URL}/api/admin-groups/users-by-group/${activeGroupId}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        const currentGroupUsers = res.data.map((u) => u.id);

        const toAdd = selectedUserIds.filter(
          (id) => !currentGroupUsers.includes(id)
        );
        const toRemove = currentGroupUsers.filter(
          (id) => !selectedUserIds.includes(id)
        );

        const affectedUserIds = [...new Set([...toAdd, ...toRemove])];

        const userDetails = await Promise.all(
          affectedUserIds.map((userId) =>
            axios
              .get(`${BASE_URL}/api/users/${userId}`, {
                headers: {
                  Authorization: `Bearer ${localStorage.getItem("token")}`,
                },
              })
              .then((res) => res.data)
          )
        );

        const updateRequests = userDetails.map((user) => {
          let parsed = [];

          try {
            const raw = user.emp_groups;

            // Handle if it's already JSON, array, or invalid
            if (Array.isArray(raw)) {
              parsed = raw.map(Number);
            } else if (typeof raw === "string" && raw.trim() !== "") {
              const temp = JSON.parse(raw);
              parsed = Array.isArray(temp) ? temp.map(Number) : [];
            }
          } catch (e) {
            parsed = [];
          }

          // Remove the group if exists
          const cleaned = parsed.filter((id) => id !== Number(activeGroupId));

          // Add group if selected
          const newGroups = selectedUserIds.includes(user.id)
            ? [...cleaned, Number(activeGroupId)]
            : cleaned;

          return axios.put(
            `${BASE_URL}/api/admin-groups/update-emp-groups/${user.id}`,
            { emp_groups: JSON.stringify(newGroups) },
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
              },
            }
          );
        });

        await Promise.all(updateRequests);
      }

      alert(`✅ Group ${isEditMode ? "updated" : "created"} successfully!`);
      navigate("/admin-groups");
    } catch (err) {
      console.error(err);
      alert(`❌ Failed to ${isEditMode ? "update" : "create"} group.`);
    }
  };

  const selectedOptions = allAdmins.filter((u) =>
    selectedUserIds.includes(u.value)
  );

  if (loading) return <div className="container mt-5">Loading...</div>;

  return (
    <>
      <div className="d-flex justify-content-between align-items-center pt-3">
        <span style={{ fontSize: "18px", fontWeight: "500" }}>
          {isEditMode ? `Edit Group - ${group.pg_name}` : "Create New Group"}
        </span>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={() => navigate("/admin-groups")}
        >
          Return
        </button>
      </div>

      <div className="form-wrapper my-4">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSave();
          }}
        >
          {/* Group Info */}
          <fieldset className="border p-3 mb-4">
            <legend className="w-auto px-2">Group Info</legend>
            <div className="row mb-3">
              <div className="col-md-6">
                <label>Group Name *</label>
                <input
                  type="text"
                  className="form-control"
                  value={group.pg_name}
                  onChange={(e) => handleChange("pg_name", e.target.value)}
                  required
                />
              </div>
              <div className="col-md-6">
                <label>Group Email</label>
                <input
                  type="email"
                  className="form-control"
                  value={group.pg_email || ""}
                  onChange={(e) => handleChange("pg_email", e.target.value)}
                />
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-md-4">
                <label>Status *</label>
                <select
                  className="form-select"
                  value={group.pg_status}
                  onChange={(e) =>
                    handleChange("pg_status", Number(e.target.value))
                  }
                >
                  <option value={1}>Active</option>
                  <option value={0}>Inactive</option>
                </select>
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-md-6 d-flex align-items-center">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="pg_notify_leads"
                    checked={!!group.pg_notify_leads}
                    onChange={(e) =>
                      handleChange("pg_notify_leads", e.target.checked ? 1 : 0)
                    }
                  />
                  <label
                    className="form-check-label ms-2"
                    htmlFor="pg_notify_leads"
                  >
                    Email when new leads are received
                  </label>
                </div>
              </div>
            </div>
          </fieldset>

          {/* Assigned Users (only in edit mode) */}
          {isEditMode && (
            <fieldset className="border p-3 mb-4">
              <legend className="w-auto px-2">Assigned Users</legend>
              <Select
                isMulti
                options={allAdmins}
                value={selectedOptions}
                onChange={handleSelectChange}
                placeholder="Select admins..."
              />
            </fieldset>
          )}

          {/* Save Button */}
          <div className="d-flex justify-content-end">
            <button type="submit" className="btn btn-primary px-5">
              Save
            </button>
          </div>
        </form>
      </div>
    </>
  );
}

export default EditGroupAdmin;
